/*
 * @Description: Use cases section for AI Video Editor - tab-based card layout with dual images
 */
import Image from 'next/image';
import React, { useCallback, useEffect, useState } from 'react';

import StartButton from './StartButton';

const CDN_BASE = 'https://assets.falcocut.ai/ai-video-editor';

interface UseCase {
  id: string;
  tab: string;
  title: string;
  description: string;
  images: [string, string];
}

const useCases: UseCase[] = [
  {
    id: 'product-swap',
    tab: 'Product Swap',
    title: 'Product Swap',
    description:
      'Replace the product in hand without reshooting. Turn one beauty demo into multiple product variations \u2014 foundation, lipstick, palette, or skincare \u2014 while keeping the same presenter and scene. Perfect for testing new SKUs, launching updates, or refreshing creatives quickly.',
    images: [
      `${CDN_BASE}/makeup-foundation-palette.png`,
      `${CDN_BASE}/makeup-lipstick-closeup.png`,
    ],
  },
  {
    id: 'product-showcase',
    tab: 'Product Showcase',
    title: 'Product Showcase',
    description:
      'Upgrade simple product shots into premium brand visuals. Transform plain packshots into polished commercial-style presentations with refined lighting, balanced composition, and elevated brand tone. Ideal for landing pages, ads, and social media campaigns.',
    images: [
      `${CDN_BASE}/skincare-with-leaves.png`,
      `${CDN_BASE}/skincare-marble-background.png`,
    ],
  },
  {
    id: 'background-change',
    tab: 'Background Change',
    title: 'Background Change',
    description:
      'Move your subject into a new environment instantly. Switch from indoor studio to retail space, outdoor adventure, mountain, or lifestyle setting \u2014 without location shoots or complex editing. Create context-driven visuals that match your campaign story.',
    images: [
      `${CDN_BASE}/goggles-store-interior.png`,
      `${CDN_BASE}/goggles-mountain-snow.png`,
    ],
  },
  {
    id: 'packaging-update',
    tab: 'Packaging Update',
    title: 'Packaging Update',
    description:
      'Update packaging without recreating the entire video. Replace old packaging with new branding, seasonal editions, or limited designs while preserving the original footage. Great for rebrands, promotional launches, and A/B testing.',
    images: [`${CDN_BASE}/dog-green-packaging.png`, `${CDN_BASE}/dog-red-packaging.png`],
  },
  {
    id: 'outfit-replacement',
    tab: 'Outfit Replacement',
    title: 'Outfit Replacement',
    description:
      'Change clothing styles to match different audiences. Swap dresses, jackets, or fashion pieces while keeping pose and framing consistent. Perfect for fashion drops, lookbook updates, and style variations within the same concept.',
    images: [`${CDN_BASE}/woman-floral-dress.png`, `${CDN_BASE}/woman-black-blazer.png`],
  },
];

const UseCasesSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  // 记录已访问过的 tab，保证其图片留在 DOM 中（浏览器缓存命中）
  const [visitedTabs, setVisitedTabs] = useState<Set<number>>(() => new Set([0]));

  // 切换 tab 时：成对预加载该 tab 的两张图片，同时标记为已访问
  const handleTabClick = useCallback((index: number) => {
    setActiveIndex(index);
    setVisitedTabs((prev) => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  }, []);

  // 首屏及切换时，程序化预加载当前 tab 的图片对
  useEffect(() => {
    useCases[activeIndex].images.forEach((url) => {
      const img = new window.Image();
      img.src = url;
    });
  }, [activeIndex]);

  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Edit Smarter. Create Without Limits
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-5xl mx-auto mb-8 pad:mb-10 sm:mb-12 leading-relaxed">
          AI Video Editor helps you transform existing videos into new creative assets
          &mdash; without reshooting, complex software, or long production cycles.
        </p>

        {/* Tabs */}
        <div
          className="flex justify-center gap-4 pad:gap-6 sm:gap-8 mb-8 pad:mb-10 overflow-x-auto"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {useCases.map((uc, index) => (
            <button
              key={uc.id}
              type="button"
              onClick={() => handleTabClick(index)}
              className={`flex-shrink-0 pb-2 text-sm pad:text-base font-semibold transition-all duration-200 border-b-2 ${
                index === activeIndex
                  ? 'text-[#2D2B55] border-[#2D2B55]'
                  : 'text-gray-400 border-transparent hover:text-gray-600'
              }`}
            >
              {uc.tab}
            </button>
          ))}
        </div>

        {/* Content Card - 所有已访问 tab 保留在 DOM，仅切换可见性 */}
        <div className="rounded-[24px] bg-[#F0F2F8] p-6 pad:p-8 sm:p-10">
          {useCases.map((uc, index) => {
            const isActive = index === activeIndex;
            // 未访问过的 tab 不渲染（懒加载）
            if (!visitedTabs.has(index)) return null;
            return (
              <div key={uc.id} className={isActive ? 'block' : 'hidden'}>
                <div className="flex flex-col sm:flex-row gap-6 pad:gap-8 sm:gap-10 items-center">
                  {/* Left: Text + CTA */}
                  <div className="w-full sm:w-[35%] flex flex-col">
                    <h3 className="text-xl pad:text-2xl font-bold text-gray-900 mb-3 pad:mb-4">
                      {uc.title}
                    </h3>
                    <p className="text-sm pad:text-base text-gray-600 leading-relaxed mb-5 pad:mb-6">
                      {uc.description}
                    </p>
                    <div>
                      <StartButton text="Edit Your Video Now" />
                    </div>
                  </div>

                  {/* Right: Two images */}
                  <div className="w-full sm:w-[65%] flex gap-3 pad:gap-4">
                    <div className="flex-1 relative rounded-2xl overflow-hidden bg-gray-200 aspect-[3/4]">
                      <Image
                        src={uc.images[0]}
                        alt={`${uc.title} - before`}
                        fill
                        className="object-cover"
                        sizes="(max-width: 768px) 50vw, 30vw"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 relative rounded-2xl overflow-hidden bg-gray-200 aspect-[3/4]">
                      <Image
                        src={uc.images[1]}
                        alt={`${uc.title} - after`}
                        fill
                        className="object-cover"
                        sizes="(max-width: 768px) 50vw, 30vw"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default UseCasesSection;
